import { useCallback, useMemo, useState } from "react"
import type React from "react"
import type { ChangeEvent } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Button } from "shared/ui/button"
import { Badge } from "shared/ui/badge"
import { FileText, Upload, CheckCircle, AlertCircle, X, Loader2, ExternalLink } from "lucide-react"
import { uploadPermitAttachment } from "modules/permit"
import { cn } from "shared/ui/utils"
import { buildAttachmentUrl } from "shared/utils/attachmentUrl"
import { buildRequiredDocumentValidationKey } from "modules/permit/pages/permit-request/utils/validationTargets"

interface RequiredDocument {
  id: string
  name: string
  code: string
  isMandatory: boolean
}

export interface UploadedFileItem {
  id: string
  docId: string
  name: string
  uploadedAt: string
  size?: number | null
  type?: string
  storagePath?: string
  fileName?: string
  storedFileName?: string
  documentCode?: string
  contentType?: string
  uploadedBy?: string | null
  status?: string
}

interface RequiredDocumentsSectionProps {
  requiredDocuments: RequiredDocument[]
  uploadedFiles: UploadedFileItem[]
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFileItem[]>>
  tenantId: string
  permitNumber?: string | null
  isLoading?: boolean
  errorMessage?: string | null
  getValidationContainerClass?: (key?: string | null) => string
}

const buildFileUrl = (path: string | undefined): string | null => buildAttachmentUrl(path)

export function RequiredDocumentsSection({
  requiredDocuments,
  uploadedFiles,
  setUploadedFiles,
  tenantId,
  permitNumber,
  isLoading = false,
  errorMessage,
  getValidationContainerClass,
}: RequiredDocumentsSectionProps) {
  const [uploadingDocId, setUploadingDocId] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [draftPermitKey] = useState(() => {
    const tenantSeed = tenantId.replace(/-/g, "").slice(0, 8) || "tenant"
    const randomSeed = Math.random().toString(36).slice(2, 8)
    return `DRAFT-${tenantSeed}-${Date.now()}-${randomSeed}`
  })
  const effectivePermitNumber = permitNumber?.trim() || draftPermitKey

  const handleFileUpload = async (doc: RequiredDocument, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    if (!tenantId) {
      setUploadError("กรุณาเลือก Tenant ก่อนอัปโหลดไฟล์")
      event.target.value = ""
      return
    }
    try {
      setUploadError(null)
      setUploadingDocId(doc.id)

      const formData = new FormData()
      formData.append("tenantId", tenantId)
      formData.append("permitNumber", effectivePermitNumber)
      if (doc.code) {
        formData.append("documentCode", doc.code)
      }
      formData.append("file", file)

      const response = await uploadPermitAttachment(formData)

      const newFile: UploadedFileItem = {
        id: `${doc.id}-${Date.now()}`,
        docId: doc.id,
        documentCode: response.documentCode ?? doc.code,
        name: response.fileName || file.name,
        size: response.size ?? file.size,
        type: response.contentType ?? file.type,
        contentType: response.contentType,
        uploadedAt: new Date().toISOString(),
        storagePath: response.storagePath,
        fileName: response.fileName || file.name,
        storedFileName: response.storedFileName,
        status: "ACTIVE",
      }

      setUploadedFiles((prev) => [...prev, newFile])
    } catch (error) {
      console.error("Failed to upload required document", error)
      setUploadError("อัปโหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง")
    } finally {
      setUploadingDocId(null)
      if (event.target) {
        event.target.value = ""
      }
    }
  }

  const handleFileRemove = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId))
  }

  const getUploadedFilesForDoc = useCallback(
    (docId: string) => uploadedFiles.filter((file) => file.docId === docId),
    [uploadedFiles],
  )

  const completedCount = useMemo(
    () => requiredDocuments.reduce((count, doc) => count + (getUploadedFilesForDoc(doc.id).length > 0 ? 1 : 0), 0),
    [requiredDocuments, getUploadedFilesForDoc],
  )

  const formatFileSize = (bytes?: number | null) => {
    if (bytes === undefined || bytes === null) {
      return "ไม่ทราบขนาด"
    }
    if (bytes === 0) {
      return "0 Bytes"
    }
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1)
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const renderDocumentList = (docs: RequiredDocument[]) => {
    if (docs.length === 0) {
      return null
    }

    return (
      <div className="space-y-4">
        {docs.map((doc) => {
          const filesForDoc = getUploadedFilesForDoc(doc.id)
          const isCompleted = filesForDoc.length > 0
          const validationKey = buildRequiredDocumentValidationKey(doc.id)
          const containerClasses = [
            "space-y-3 rounded-lg border p-4",
            isCompleted ? "border-gray-200" : doc.isMandatory ? "border-red-300 bg-red-50" : "border-gray-200",
            getValidationContainerClass?.(validationKey) ?? "",
          ].join(" ")

          return (
            <div key={doc.id} className={cn(containerClasses)} data-validation-key={validationKey}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h5 className="font-medium text-gray-900">{doc.name}</h5>
                    {doc.isMandatory ? (
                      <Badge variant="secondary" className="text-xs text-red-600">
                        จำเป็น
                      </Badge>
                    ) : null}
                    {isCompleted ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {isCompleted ? "อัปโหลดแล้ว" : doc.isMandatory ? "จำเป็นต้องแนบไฟล์" : "ยังไม่ได้อัปโหลด"}
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileUpload(doc, e)}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    disabled={uploadingDocId === doc.id}
                  />
                  <Button size="sm" variant="outline" disabled={uploadingDocId === doc.id}>
                    {uploadingDocId === doc.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        กำลังอัปโหลด...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {filesForDoc.length > 0 ? (
                <div className="space-y-2">
                  {filesForDoc.map((file) => {
                    const fileUrl = buildFileUrl(file.storagePath)

                    return (
                      <div
                        key={file.id}
                        className="flex items-center justify-between rounded border border-blue-200 bg-blue-50 p-2"
                      >
                        <div className="flex flex-1 items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">{file.name}</p>
                            <p className="text-xs text-blue-700">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {fileUrl ? (
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              เปิด
                            </a>
                          ) : null}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleFileRemove(file.id)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-6">
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="h-5 w-5 text-blue-500" />
          เอกสารที่ต้องแนบ
          <Badge variant="secondary" className="ml-auto">
            {completedCount} / {requiredDocuments.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {!permitNumber ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            แนบไฟล์ได้ทันทีในโหมดสร้างเอกสาร ระบบจะเก็บไฟล์ไว้ชั่วคราวและเชื่อมกับ Permit เมื่อบันทึกครั้งแรก
          </div>
        ) : null}
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            กำลังโหลดรายการเอกสาร...
          </div>
        ) : null}
        {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
        {uploadError ? <p className="text-sm text-red-600">{uploadError}</p> : null}
        {requiredDocuments.length === 0 && !isLoading ? (
          <div className="py-8 text-center text-sm text-gray-500">
            <p>ยังไม่มีรายการเอกสารที่ต้องแนบเพิ่มเติม</p>
            <p className="text-xs text-gray-400">ระบบจะแสดงส่วนนี้เมื่อมีการกำหนดเอกสารที่จำเป็น</p>
          </div>
        ) : (
          renderDocumentList(requiredDocuments)
        )}
      </CardContent>
    </Card>
  )
}
